<template>
  <div class="voice-download">
    <h1>音声ダウンロード</h1>
    <div v-if="loading" class="loading">
      <div class="spinner"></div>
      <p>読み込み中...</p>
    </div>
    <div v-else-if="error" class="error">
      <p>{{ error }}</p>
    </div>
    <div v-else>
      <div v-if="isMultipleVoices">
        <h2 class="mt-5">複数の音声ファイルが<br />見つかりました</h2>
        <div class="voice-list">
          <div v-for="(voice, index) in voiceList" :key="index" class="voice-item">
            <p>音声 {{ index + 1 }}</p>
            <button @click="downloadSpecificVoice(voice)" class="download-button">
              ダウンロード
            </button>
          </div>
        </div>
      </div>
      <div v-else>
        <p>イベントID: {{ eventid }}</p>
        <p>音声ID: {{ voiceid }}</p>
        <button @click="downloadVoice" class="download-button">ダウンロード</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { getStorage, ref as storageRef, getDownloadURL } from "firebase/storage";
import { getAuth, onAuthStateChanged } from "firebase/auth";

const route = useRoute();
const router = useRouter();
const eventid = route.query.eventid;
const voiceid = route.query.voiceid;
const loading = ref(true);
const error = ref(null);
const downloadUrl = ref(null);
const isAuthenticated = ref(false);
const isMultipleVoices = ref(false);
const voiceList = ref([]);
const currentUser = ref(null);

onMounted(async () => {
  // パラメータのバリデーション
  if (!eventid || !voiceid) {
    error.value = "このURLにはボイスはありません";
    loading.value = false;
    return;
  }

  const auth = getAuth();

  onAuthStateChanged(auth, async (user) => {
    if (user) {
      isAuthenticated.value = true;
      currentUser.value = user;

      // 特殊なケース: special001で、ユーザーごとに異なる音声を提供
      if (voiceid === "special001") {
        await handleUserSpecificVoice(user.uid);
      } else {
        // 単一の音声ファイルを処理
        await handleSingleVoice();
      }
    } else {
      // 未ログインの場合はログインページにリダイレクト
      router.push({ name: "TheLogin", query: { redirect: route.fullPath } });
    }
  });
});

// ユーザー固有の音声を処理
const handleUserSpecificVoice = async (uid) => {
  try {
    const storage = getStorage();
    const voiceFiles = [];

    // ユーザー固有のファイルを探す
    // {uid}-1.wav, {uid}-2.wav などの形式
    try {
      // まず1つ目のファイルをチェック
      const firstVoiceUrl = await fetchVoiceUrl(storage, `${uid}-1`);
      voiceFiles.push({ id: `${uid}-1`, url: firstVoiceUrl });

      // 2つ目のファイルも存在するかチェック
      try {
        const secondVoiceUrl = await fetchVoiceUrl(storage, `${uid}-2`);
        voiceFiles.push({ id: `${uid}-2`, url: secondVoiceUrl });

        // 必要に応じて3つ目以降もチェック可能
      } catch (err) {
        // 2つ目のファイルがなければスキップ
        console.log("Second voice file not found for user:", uid);
      }
    } catch (err) {
      console.log("No voice files found for user:", uid);
      // ユーザー固有のファイルが見つからない場合はエラーを表示
      throw new Error("音声ファイルが見つかりません");
    }

    // 見つかったファイルの数に応じて処理
    if (voiceFiles.length === 0) {
      // ファイルが見つからない
      throw new Error("音声ファイルが見つかりません");
    } else if (voiceFiles.length === 1) {
      // 1つだけ見つかった場合は自動ダウンロード
      downloadUrl.value = voiceFiles[0].url;
      loading.value = false;
      downloadVoice(voiceFiles[0].id);
    } else {
      // 複数見つかった場合はリスト表示
      voiceList.value = voiceFiles;
      isMultipleVoices.value = true;
      loading.value = false;
    }
  } catch (err) {
    console.error("Error fetching user specific voice:", err);
    error.value =
      err.message === "音声ファイルが見つかりません"
        ? err.message
        : "このURLにはボイスはありません";
    loading.value = false;
  }
};

// 単一の音声ファイルを処理
const handleSingleVoice = async () => {
  try {
    const storage = getStorage();
    downloadUrl.value = await fetchVoiceUrl(storage, voiceid);
    loading.value = false;
    // 自動的にダウンロードを開始
    downloadVoice();
  } catch (err) {
    console.error("Error fetching file:", err);
    error.value = "このURLにはボイスはありません";
    loading.value = false;
  }
};

// 音声ファイルのURLを取得する共通関数
const fetchVoiceUrl = async (storage, id) => {
  try {
    // 従来の方法でURLを取得
    const storagePath = `gs://rf-prd-sukisuki-ls.appspot.com/voices/${eventid}/${id}.wav`;
    const voiceRef = storageRef(storage, storagePath);
    return await getDownloadURL(voiceRef);
  } catch (storageErr) {
    // 失敗した場合、直接URLを構築する代替方法を使用
    console.log("Storage access failed, using direct URL");
    // Cloud Storage直接アクセスURL
    const directUrl = `https://storage.googleapis.com/rf-prd-sukisuki-ls.appspot.com/voices/${eventid}/${id}.wav`;

    // ファイルが存在するか確認
    const response = await fetch(directUrl, { method: "HEAD" });
    if (response.ok) {
      return directUrl;
    } else {
      throw new Error("ファイルが存在しません");
    }
  }
};

// 特定の音声をダウンロード
const downloadSpecificVoice = (voice) => {
  if (voice && voice.url) {
    try {
      const link = document.createElement("a");
      link.href = voice.url;
      link.download = `voice_${eventid}_${voice.id}.wav`;

      link.onerror = () => {
        throw new Error("ダウンロード中にエラーが発生しました");
      };

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      error.value = "このURLにはボイスはありません";
      loading.value = false;
    }
  }
};

const downloadVoice = (specificId = null) => {
  if (downloadUrl.value) {
    try {
      const link = document.createElement("a");
      link.href = downloadUrl.value;
      // 指定されたIDがあればそれを使用、なければvoiceidを使用
      const fileId = specificId || voiceid;
      link.download = `voice_${eventid}_${fileId}.wav`;

      // リンクをクリックする前にエラーをキャッチできるよう、onエラーハンドラを追加
      link.onerror = () => {
        throw new Error("ダウンロード中にエラーが発生しました");
      };

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // ダウンロード完了後の処理
      setTimeout(() => {
        router.push({ name: "TheMainView" });
      }, 1000);
    } catch (err) {
      error.value = "このURLにはボイスはありません";
      loading.value = false;
    }
  }
};
</script>

<style scoped>
.voice-download {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.loading {
  text-align: center;
  margin: 20px 0;
}

.spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto;
  border-radius: 50%;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.error {
  color: red;
  text-align: center;
  margin: 20px 0;
}

.voice-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 20px 0;
}

.voice-item {
  padding: 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background-color: #f9f9f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.download-button {
  display: block;
  margin: 0 auto;
  padding: 10px 20px;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
}

.download-button:hover {
  background-color: #45a049;
}

.voice-download h2 {
  font-size: 1.3rem;
  line-height: 1.8;
  text-align: center;
  margin-top: 2em;
  margin-bottom: 1em;
}
</style>
